import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { staffAPI } from '../services/api';
import {
  LoadingSpinner,
  EmptyState,
  StatCard,
  Badge,
  Modal,
  ConfirmDialog,
  DataTable,
  Pagination,
} from '../components/common';
import { formatDate, formatPhone } from '../utils/formatters';

export default function StaffPage() {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAuditLogsModalOpen, setIsAuditLogsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: staffData, isLoading } = useQuery({
    queryKey: ['staff', currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      const response = await staffAPI.getAll(params);
      return response.data;
    },
  });

  const staff = staffData?.data || [];
  const pagination = staffData?.pagination || { total: 0, pages: 1 };
  console.log(staff);

  const { data: auditLogs } = useQuery({
    queryKey: ['audit-logs'],
    queryFn: async () => {
      const response = await staffAPI.getAuditLogs();
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
    enabled: isAuditLogsModalOpen,
  });

  const createMutation = useMutation({
    mutationFn: (data) => staffAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      toast.success('Staff member created successfully!');
      setIsCreateModalOpen(false);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create staff');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => staffAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      toast.success('Staff member updated successfully!');
      setIsEditModalOpen(false);
      setSelectedStaff(null);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update staff');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => staffAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      toast.success('Staff member deleted successfully!');
      setIsDeleteModalOpen(false);
      setStaffToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete staff');
    },
  });

  const handleEdit = (staffMember) => {
    setSelectedStaff(staffMember);
    setValue('phone', staffMember.phone.replace(/^\+251/, ''));
    setValue('role', staffMember.role);
    setValue('language', staffMember.language);
    setIsEditModalOpen(true);
  };

  const handleDelete = (staffMember) => {
    setStaffToDelete(staffMember);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (staffToDelete) {
      deleteMutation.mutate(staffToDelete.id);
    }
  };

  const onCreateSubmit = (data) => {
    createMutation.mutate({
      phone: data.phone,
      role: data.role,
      language: data.language,
    });
  };

  const onEditSubmit = (data) => {
    updateMutation.mutate({
      id: selectedStaff.id,
      data: {
        phone: data.phone,
        role: data.role,
        language: data.language,
      },
    });
  };

  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (value) => formatPhone(value),
    },
    {
      key: 'role',
      label: 'Role',
      render: (value, staffMember) => (
        <div className="flex items-center gap-2">
          <Badge variant={
            value === 'admin' ? 'danger' :
            value === 'staff' ? 'warning' :
            'info'
          }>
            {value}
          </Badge>
          {staffMember.deleted_at && (
            <Badge variant="default">
              Deleted
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'language',
      label: 'Language',
      render: (value) => value?.toUpperCase() || '-',
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, staffMember) => {
        const isDeleted = staffMember.deleted_at !== null;
        return (
          <div className="flex gap-2">
            <button
              onClick={() => handleEdit(staffMember)}
              disabled={isDeleted}
              className={`text-sm font-medium ${
                isDeleted 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-primary-600 hover:text-primary-800'
              }`}
              title={isDeleted ? 'Cannot edit deleted staff' : 'Edit staff'}
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleDelete(staffMember)}
              disabled={isDeleted}
              className={`text-sm font-medium ${
                isDeleted 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-red-600 hover:text-red-800'
              }`}
              title={isDeleted ? 'Already deleted' : 'Delete staff'}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ];

  const stats = {
    total: staff?.length || 0,
    admins: staff?.filter(s => s.role === 'admin').length || 0,
    staff: staff?.filter(s => s.role === 'staff').length || 0,
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading staff..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Staff Management</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage staff members and view audit logs
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsAuditLogsModalOpen(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <ClipboardDocumentListIcon className="h-5 w-5" />
            Audit Logs
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Add Staff
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Total Staff"
          value={stats.total}
          icon={UserGroupIcon}
          color="primary"
        />
        <StatCard
          title="Admins"
          value={stats.admins}
          icon={UserGroupIcon}
          color="red"
        />
        <StatCard
          title="Staff"
          value={stats.staff}
          icon={UserGroupIcon}
          color="yellow"
        />
      </div>

      <div className="card">
        {staff && staff.length > 0 ? (
          <>
            <DataTable columns={columns} data={staff} />
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
            />
          </>
        ) : (
          <EmptyState
            icon={UserGroupIcon}
            title="No staff members"
            description="Add your first staff member to get started"
            action={() => setIsCreateModalOpen(true)}
            actionLabel="Add Staff"
          />
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          reset();
        }}
        title="Add Staff Member"
        size="md"
      >
        <form onSubmit={handleSubmit(onCreateSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number * (9 digits)
            </label>
            <input
              {...register('phone', {
                required: 'Phone is required',
                pattern: {
                  value: /^[0-9]{9}$/,
                  message: 'Phone must be 9 digits'
                }
              })}
              type="text"
              className="input-field"
              placeholder="911234567"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              {...register('role', { required: 'Role is required' })}
              className="input-field"
            >
              <option value="">Select role...</option>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language *
            </label>
            <select
              {...register('language', { required: 'Language is required' })}
              className="input-field"
            >
              <option value="">Select language...</option>
              <option value="en">English</option>
              <option value="am">Amharic</option>
              <option value="om">Oromo</option>
              <option value="ti">Tigrinya</option>
            </select>
            {errors.language && (
              <p className="mt-1 text-sm text-red-600">{errors.language.message}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsCreateModalOpen(false);
                reset();
              }}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {createMutation.isLoading ? 'Creating...' : 'Create Staff'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedStaff(null);
          reset();
        }}
        title="Edit Staff Member"
        size="md"
      >
        <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number * (9 digits)
            </label>
            <input
              {...register('phone', {
                required: 'Phone is required',
                pattern: {
                  value: /^[0-9]{9}$/,
                  message: 'Phone must be 9 digits'
                }
              })}
              type="text"
              className="input-field"
              placeholder="911234567"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role *
            </label>
            <select
              {...register('role', { required: 'Role is required' })}
              className="input-field"
            >
              <option value="">Select role...</option>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language *
            </label>
            <select
              {...register('language', { required: 'Language is required' })}
              className="input-field"
            >
              <option value="">Select language...</option>
              <option value="en">English</option>
              <option value="am">Amharic</option>
              <option value="om">Oromo</option>
              <option value="ti">Tigrinya</option>
            </select>
            {errors.language && (
              <p className="mt-1 text-sm text-red-600">{errors.language.message}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsEditModalOpen(false);
                setSelectedStaff(null);
                reset();
              }}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {updateMutation.isLoading ? 'Updating...' : 'Update Staff'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Audit Logs Modal */}
      <Modal
        isOpen={isAuditLogsModalOpen}
        onClose={() => setIsAuditLogsModalOpen(false)}
        title="Audit Logs"
        size="lg"
      >
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {auditLogs && auditLogs.length > 0 ? (
            auditLogs.map((log) => (
              <div key={log.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{log.action}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      By: {log.actor_phone || `User #${log.actor_id}`} • {formatDate(log.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <EmptyState
              icon={ClipboardDocumentListIcon}
              title="No audit logs"
              description="Audit logs will appear here"
            />
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setStaffToDelete(null);
        }}
        title="Delete Staff Member?"
        size="md"
      >
        <div className="space-y-4">
          {/* Warning Icon */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="h-24 w-24 rounded-full bg-red-100 flex items-center justify-center">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-600" />
              </div>
              <div className="absolute inset-0 rounded-full bg-red-500 bg-opacity-10 animate-ping" />
            </div>
          </div>

          {/* Staff Info */}
          {staffToDelete && (
            <div className="text-center space-y-2">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Phone Number</p>
                <p className="text-lg font-semibold text-gray-900">{formatPhone(staffToDelete.phone)}</p>
                <Badge variant={
                  staffToDelete.role === 'admin' ? 'danger' : 'warning'
                } className="mt-2">
                  {staffToDelete.role}
                </Badge>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="text-center space-y-2">
            <p className="text-base font-semibold text-gray-900">
              This action cannot be undone!
            </p>
            <p className="text-sm text-gray-600">
              Deleting this staff member will remove all their access and permissions.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setStaffToDelete(null);
              }}
              className="flex-1 btn-secondary"
              disabled={deleteMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleteMutation.isLoading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
            >
              {deleteMutation.isLoading ? 'Deleting...' : 'Yes, Delete Staff'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
